import { useState, useEffect, useMemo, useRef } from 'react'
import { LandingPageData } from '@/types/main'
import { mainScreenService } from '@/services/mainScreenService'

export const useMainScreen = (params?: Record<string, any>) => {
  const [data, setData] = useState<LandingPageData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const paramsRef = useRef(params)
  
  // Memoize params to prevent unnecessary re-renders
  const stableParams = useMemo(() => params, [
    params?.limit,
    params?.offset,
    params?.search,
    params?.type,
    params?.industry
  ])

  useEffect(() => {
    // Only refetch if params actually changed
    const paramsChanged = JSON.stringify(paramsRef.current) !== JSON.stringify(stableParams)
    if (!paramsChanged && data !== null) {
      return
    }
    
    paramsRef.current = stableParams
    let isMounted = true
    
    const loadData = async () => {
      try {
        setLoading(true)
        const result = await mainScreenService.getLandingPageData(stableParams)
        if (isMounted) {
          setData(result)
          setError(null)
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to load data')
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    loadData()
    
    return () => {
      isMounted = false
    }
  }, [stableParams, data])

  return { data, loading, error }
} 